import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Printer, FileText, Eye, Loader2 } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';

// Phase 2.5 Shipping Label Print — 단건/다건 미리보기 + 100x150 / A4 + 재출력.
// No status/inventory mutations. Only /shipping-labels/preview + /print + history.

export default function ShippingLabelModal({ open, orderIds, onClose }) {
  const [size, setSize] = useState('100x150');
  const [labels, setLabels] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [stage, setStage] = useState('preview'); // preview | printed
  const [busy, setBusy] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (!open || !orderIds?.length) return;
    setStage('preview'); setLabels([]); setSkipped([]);
    (async () => {
      setBusy(true);
      try {
        const r = await api.post('/seller/shipping-labels/preview', { seller_order_ids: orderIds, size });
        setLabels(r.data.labels || []); setSkipped(r.data.skipped || []);
      } catch (e) { toast.error(e?.response?.data?.detail || '미리보기 실패'); }
      finally { setBusy(false); }
    })();
  }, [open, orderIds, size]);

  if (!open) return null;

  const doPrint = async () => {
    if (labels.length === 0) { toast.error('출력할 주문이 없습니다.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/seller/shipping-labels/print', { seller_order_ids: labels.map((l) => l.seller_order_id), size });
      setLabels(r.data.labels || []); setStage('printed');
      // Trigger browser print
      setTimeout(() => window.print(), 300);
      toast.success(`${r.data.printed_count}건 출력 완료`);
    } catch (e) { toast.error(e?.response?.data?.detail || '출력 실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex bg-black/60 label-modal-root" data-testid="shipping-label-modal">
      <div className="flex-1" onClick={onClose} />
      <div className="w-full max-w-4xl bg-white dark:bg-[#0A0A0A] h-full overflow-y-auto shadow-2xl label-print-root">
        <div className="sticky top-0 z-10 bg-white dark:bg-[#0A0A0A] border-b border-black/10 dark:border-white/10 p-3 flex justify-between items-center print-hide">
          <div>
            <p className="text-sm font-black">송장 출력 · {size === '100x150' ? '100 × 150 라벨' : 'A4'}</p>
            <p className="text-[11px] text-gray-500">{labels.length}건 출력 대상 · 스킵 {skipped.length}건</p>
          </div>
          <div className="flex gap-2 items-center">
            <select value={size} onChange={(e) => setSize(e.target.value)} className="px-2 py-1 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent" data-testid="lbl-size">
              <option value="100x150">100 × 150 라벨</option>
              <option value="A4">A4</option>
            </select>
            <button onClick={doPrint} disabled={busy || labels.length === 0} className="px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black inline-flex items-center gap-1 disabled:opacity-40" data-testid="lbl-print-btn">
              <Printer size={11}/> {stage === 'printed' ? '재출력' : '출력'} ({labels.length})
            </button>
            <button onClick={onClose} className="p-1" data-testid="lbl-close"><X size={16}/></button>
          </div>
        </div>

        {skipped.length > 0 && (
          <div className="print-hide p-3 mx-3 mt-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 text-xs" data-testid="lbl-skipped">
            <p className="font-black text-amber-700 dark:text-amber-300 mb-1">출력 제외 {skipped.length}건</p>
            {skipped.map((s, i) => (
              <p key={i} className="text-amber-700 dark:text-amber-300">{s.id} · {s.reason}</p>
            ))}
          </div>
        )}

        {busy && labels.length === 0 && (
          <div className="p-12 text-center text-sm text-gray-400 print-hide"><Loader2 className="animate-spin inline mr-1" size={14}/> 미리보기 로딩…</div>
        )}

        <div ref={printRef} className={`p-4 label-sheet label-size-${size === '100x150' ? 'small' : 'a4'}`} data-testid="lbl-preview-root">
          {labels.map((lb, i) => (
            <LabelCard key={lb.seller_order_id} label={lb} size={size} index={i} />
          ))}
        </div>
      </div>

      {/* Print CSS — kept scoped to this modal. */}
      <style>{`
        @media print {
          body > *:not(.label-modal-root) { display: none !important; }
          .label-modal-root { position: static !important; background: white !important; }
          .print-hide { display: none !important; }
          .label-print-root { max-width: 100% !important; width: 100% !important; height: auto !important; overflow: visible !important; box-shadow: none !important; }
          .label-sheet { padding: 0 !important; }
          .label-card { page-break-after: always; break-after: page; }
          .label-size-small .label-card { width: 100mm; height: 150mm; }
          .label-size-a4 .label-card { width: 210mm; min-height: 297mm; padding: 15mm; }
        }
        .label-size-small .label-card {
          width: 100mm; min-height: 150mm; margin: 0 auto 8mm; background: white; color: #000;
          border: 2px solid #000; padding: 6mm; box-sizing: border-box; font-family: system-ui, sans-serif;
        }
        .label-size-a4 .label-card {
          width: 210mm; min-height: 250mm; margin: 0 auto 12mm; background: white; color: #000;
          border: 2px solid #000; padding: 15mm; box-sizing: border-box; font-family: system-ui, sans-serif;
        }
      `}</style>
    </div>
  );
}

function LabelCard({ label, size, index }) {
  const bcRef = useRef();
  useEffect(() => {
    if (bcRef.current && label.tracking_number) {
      try {
        JsBarcode(bcRef.current, label.tracking_number, {
          format: 'CODE128', displayValue: false,
          width: size === '100x150' ? 1.6 : 2.2, height: size === '100x150' ? 40 : 60, margin: 0,
        });
      } catch {}
    }
  }, [label.tracking_number, size]);

  const small = size === '100x150';
  return (
    <div className="label-card" data-testid={`lbl-card-${label.seller_order_id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #000', paddingBottom: 6, marginBottom: 8 }}>
        <div style={{ fontWeight: 900, fontSize: small ? 16 : 22, letterSpacing: -0.5 }}>LIVEMOA</div>
        <div style={{ textAlign: 'right', fontSize: small ? 10 : 12 }}>
          <div>주문번호</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{label.order_number}</div>
        </div>
      </div>

      {/* Barcode */}
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: small ? 10 : 12, fontWeight: 700 }}>{label.courier}</div>
        <svg ref={bcRef} data-testid={`lbl-barcode-${label.seller_order_id}`}></svg>
        <div style={{ fontFamily: 'monospace', fontSize: small ? 11 : 14, fontWeight: 700, letterSpacing: 1 }}>{label.tracking_number}</div>
      </div>

      {/* Recipient */}
      <div style={{ border: '1px solid #000', padding: small ? 4 : 8, marginBottom: 6, fontSize: small ? 10 : 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontWeight: 700 }}>받는 사람</span>
          <span style={{ fontWeight: 900, fontSize: small ? 12 : 15 }}>{label.recipient.name || '-'}</span>
        </div>
        <div>[{label.recipient.postal_code || '-'}] {label.recipient.address1 || ''} {label.recipient.address2 || ''}</div>
        <div style={{ fontFamily: 'monospace' }}>{label.recipient.phone || '-'}</div>
        {label.recipient.memo && <div style={{ marginTop: 3, fontStyle: 'italic', color: '#666' }}>배송메모: {label.recipient.memo}</div>}
      </div>

      {/* Items */}
      <div style={{ border: '1px solid #000', padding: small ? 4 : 8, marginBottom: 6, fontSize: small ? 9 : 11 }}>
        <div style={{ fontWeight: 700, marginBottom: 3 }}>상품 ({label.items.length}종)</div>
        {label.items.slice(0, small ? 3 : 8).map((it, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '75%' }}>
              {it.name} {Object.entries(it.option || {}).length > 0 ? '('+Object.entries(it.option).map(([k,v]) => v).join('·')+')' : ''}
            </span>
            <span style={{ fontWeight: 700 }}>× {it.quantity}</span>
          </div>
        ))}
        {label.items.length > (small ? 3 : 8) && <div style={{ color: '#888' }}>외 {label.items.length - (small ? 3 : 8)}건</div>}
      </div>

      {/* Sender */}
      <div style={{ padding: small ? '2px 4px' : '4px 8px', fontSize: small ? 9 : 11, borderTop: '1px dashed #999' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>보내는 사람: <b>{label.sender.brand_name || '-'}</b></span>
          <span style={{ fontFamily: 'monospace' }}>{label.sender.phone || '-'}</span>
        </div>
        {(label.sender.return_address || label.sender.return_postal_code) && (
          <div style={{ color: '#666' }}>반품: [{label.sender.return_postal_code}] {label.sender.return_address}</div>
        )}
      </div>

      {/* QR */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <div style={{ textAlign: 'center' }}>
          <QRCodeSVG value={label.qr_payload} size={small ? 48 : 72} level="M" data-testid={`lbl-qr-${label.seller_order_id}`}/>
          <div style={{ fontSize: 7, color: '#666', marginTop: 2 }}>LIVEMOA 내부 식별</div>
        </div>
      </div>
    </div>
  );
}
